/*
 * FEVR Penalty Shootout - authoritative game core.
 *
 * The server (Cloudflare Worker) and the offline practice mode in the browser both run this
 * same code. In production the CLIENT NEVER DECIDES ANYTHING that matters: it only sends
 * "I aimed here and tapped at this moment". The core owns:
 *   - the random numbers (keeper behaviour, shot scatter)  -> deps.rand
 *   - the lives, score, keeper level and attempt counter    -> session state in the store
 *   - timing validation (a client can't claim it waited longer than it really did)
 *   - nickname validation and run verification flags
 *
 * deps = {
 *   store, now(), rand(sid, attempt) -> [8 floats], newId(), newKey(), config: {...}
 * }
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory(require('./rules.js'));
  else root.FevrCore = factory(root.FevrRules);
})(typeof self !== 'undefined' ? self : this, function (Rules) {
  'use strict';

  var DEFAULTS = {
    dropId: 'drop-1',
    dropLabel: '',
    dropEndsAt: null,          // epoch ms or null
    firstDelayMs: 1600,        // before the first meter starts
    settleMs: 1900,            // result animation -> next meter start
    levelUpSettleMs: 2700,     // same, when the keeper evolves
    tolMs: 300,                // clock-drift tolerance on the client-reported timing
    minTapMs: 120,             // nobody reacts faster than this
    maxIdleMs: 15 * 60 * 1000, // abandon a session after 15 min without a shot
    minShotGapMs: 900,         // physical lower bound between two shots
    sessionsPerIpPerHour: 120, // generous: unlimited replays, this only stops scripted floods
    leaderboardLimit: 10
  };

  var BAD_WORDS = ['FUCK', 'SHIT', 'CUNT', 'NIGGER', 'NIGGA', 'FAGGOT', 'RAPE', 'NAZI', 'HITLER', 'BITCH', 'WANK', 'TWAT', 'PAKI', 'SLUT', 'WHORE', 'DICK', 'COCK', 'PUSSY', 'RETARD', 'KKK'];

  function leet(s) { return s.replace(/0/g, 'O').replace(/1/g, 'I').replace(/3/g, 'E').replace(/4/g, 'A').replace(/5/g, 'S').replace(/7/g, 'T').replace(/\$/g, 'S').replace(/@/g, 'A'); }

  function cleanNickname(raw) {
    if (typeof raw !== 'string') return null;
    var n = raw.normalize ? raw.normalize('NFKC') : raw;
    n = n.replace(/\s+/g, ' ').trim().toUpperCase();
    if (!/^[A-Z0-9 _.\-]{2,12}$/.test(n)) return null;
    var squashed = leet(n).replace(/[^A-Z]/g, '');
    for (var i = 0; i < BAD_WORDS.length; i++) if (squashed.indexOf(BAD_WORDS[i]) !== -1) return null;
    return n;
  }

  function cleanEmail(raw) {
    if (typeof raw !== 'string') return null;
    var e = raw.trim().toLowerCase();
    if (e.length < 6 || e.length > 254) return null;
    if (!/^[a-z0-9._%+\-']+@[a-z0-9\-]+(\.[a-z0-9\-]+)*\.[a-z]{2,}$/.test(e)) return null;
    if (e.indexOf('..') !== -1) return null;
    return e;
  }

  function createGame(deps) {
    var cfg = Object.assign({}, DEFAULTS, deps.config || {});
    var store = deps.store;
    var now = deps.now;

    function keeperView(level) {
      var k = Rules.KEEPERS[level];
      return { index: level, id: k.id, name: k.name, tag: k.tag };
    }

    function stateView(s) {
      return {
        attempt: s.attempt,
        lives: s.lives,
        score: s.score,
        streak: s.streak || 0,
        mult: Rules.multFor(s.streak || 0),
        level: s.level,
        over: s.over,
        keeper: keeperView(s.level),
        meter: Rules.meterInfo(s.level)
      };
    }

    function err(code, status, extra) {
      return Object.assign({ ok: false, error: code, http: status || 400 }, extra || {});
    }

    function dropClosed() {
      return cfg.dropEndsAt != null && now() > cfg.dropEndsAt;
    }

    async function start(info) {
      info = info || {};
      if (dropClosed()) return err('drop_closed', 403);
      if (info.ipHash && cfg.sessionsPerIpPerHour > 0) {
        var recent = await store.countRecentSessions(info.ipHash, now() - 3600 * 1000);
        if (recent >= cfg.sessionsPerIpPerHour) return err('rate_limited', 429);
      }
      var sid = deps.newId();
      var key = deps.newKey();
      var s = {
        sid: sid, key: key, dropId: cfg.dropId, ipHash: info.ipHash || null,
        createdAt: now(), lastAt: now(), readyAt: now() + cfg.firstDelayMs,
        attempt: 0, lives: Rules.LIVES, score: 0, streak: 0, level: 0, over: false, submitted: false,
        shots: [], version: 0
      };
      await store.createSession(s);
      return {
        ok: true, sid: sid, key: key,
        drop: { id: cfg.dropId, label: cfg.dropLabel, endsAt: cfg.dropEndsAt },
        rules: { shotsPerLevel: Rules.SHOTS_PER_LEVEL, lives: Rules.LIVES, points: Rules.POINTS },
        state: stateView(s),
        nextDelayMs: cfg.firstDelayMs
      };
    }

    async function shot(sid, key, body) {
      var s = await store.getSession(sid);
      if (!s || s.key !== key) return err('no_session', 404);
      body = body || {};

      // Idempotent retry of a shot we already resolved (lost response, double tap...)
      if (Number.isInteger(body.attempt) && body.attempt >= 0 && body.attempt < s.attempt) {
        var prev = s.shots[body.attempt];
        return prev ? prev.response : err('bad_attempt', 409);
      }
      if (s.over) return err('game_over', 409);
      if (body.attempt !== s.attempt) return err('bad_attempt', 409);
      if (dropClosed()) return err('drop_closed', 403);

      var ax = body.ax, ay = body.ay, t = body.t;
      if (![ax, ay, t].every(function (v) { return typeof v === 'number' && isFinite(v); })) return err('bad_input');
      if (ax < -1 || ax > 1 || ay < 0 || ay > 1) return err('bad_input');
      if (t < cfg.minTapMs || t > 5 * 60 * 1000) return err('bad_timing');

      var t0 = now();
      if (t0 - s.lastAt > cfg.maxIdleMs) return err('session_expired', 410);
      var elapsed = t0 - s.readyAt;                // real time since the meter was allowed to start
      if (elapsed < 0) return err('too_early', 425);
      if (t > elapsed + cfg.tolMs) return err('bad_timing');  // client claims it waited longer than physically possible
      if (s.shots.length && t0 - s.shots[s.shots.length - 1].at < cfg.minShotGapMs) return err('too_fast', 429);

      var level = Rules.levelFor(s.attempt);
      var period = Rules.KEEPERS[level].period;
      var m = Rules.meterPos(t, period);
      var rnd = await deps.rand(sid, s.attempt);
      var res = Rules.resolveShot(level, { x: ax, y: ay }, m, rnd);

      var pts = res.goal ? Rules.pointsFor(s.streak || 0) : 0; // streak bonus: consecutive goals are worth more
      var next = {
        attempt: s.attempt + 1,
        lives: s.lives - (res.goal ? 0 : 1),
        score: s.score + pts,
        streak: res.goal ? (s.streak || 0) + 1 : 0
      };
      next.over = next.lives <= 0 || next.attempt >= Rules.MAX_ATTEMPTS;
      next.level = Rules.levelFor(next.attempt);
      var levelUp = !next.over && next.level > s.level;
      var delay = next.over ? 0 : (levelUp ? cfg.levelUpSettleMs : cfg.settleMs);

      var response = {
        ok: true,
        shot: res,
        scoreDelta: pts,
        levelUp: levelUp,
        nextKeeper: levelUp ? keeperView(next.level) : null,
        state: Object.assign(stateView(Object.assign({}, s, next)), { over: next.over }),
        nextDelayMs: delay
      };

      var shots = s.shots.concat([{ a: s.attempt, at: t0, t: Math.round(t), ax: ax, ay: ay, m: res.m, perfect: res.perfect, result: res.result, response: response }]);
      var okWrite = await store.updateSession(sid, s.version, {
        attempt: next.attempt, lives: next.lives, score: next.score, streak: next.streak, level: next.level, over: next.over,
        lastAt: t0, readyAt: t0 + delay, shots: shots
      });
      if (!okWrite) return err('conflict', 409);
      return response;
    }

    function reviewFlags(s) {
      var flags = [];
      var n = s.shots.length;
      if (n >= 15) { // humans slip on the harder keepers; a long run of near-100% green-zone hits is suspicious
        var perfectRate = s.shots.filter(function (x) { return x.perfect; }).length / n;
        if (perfectRate >= 0.95) flags.push('perfect_rate');
      }
      if (n >= 8) {
        var devs = s.shots.map(function (x) { return x.m - 0.5; });
        var mean = devs.reduce(function (a, b) { return a + b; }, 0) / n;
        var sd = Math.sqrt(devs.reduce(function (a, b) { return a + (b - mean) * (b - mean); }, 0) / n);
        if (sd < 0.004) flags.push('robotic_timing');
        var gaps = [];
        for (var i = 1; i < n; i++) gaps.push(s.shots[i].at - s.shots[i - 1].at);
        var gm = gaps.reduce(function (a, b) { return a + b; }, 0) / gaps.length;
        var gsd = Math.sqrt(gaps.reduce(function (a, b) { return a + (b - gm) * (b - gm); }, 0) / gaps.length);
        if (gsd < 25) flags.push('metronome_pace');
      }
      return flags;
    }

    async function submit(sid, key, body) {
      var s = await store.getSession(sid);
      if (!s || s.key !== key) return err('no_session', 404);
      if (!s.over) return err('not_finished', 409);
      if (s.submitted) return err('already_submitted', 409);
      if (cfg.dropEndsAt != null && now() > cfg.dropEndsAt + 5 * 60 * 1000) return err('drop_closed', 403);
      var nick = cleanNickname((body || {}).nickname);
      if (!nick) return err('bad_nickname');
      var email = cleanEmail((body || {}).email);
      if (!email) return err('bad_email');
      var okMark = await store.updateSession(sid, s.version, { submitted: true });
      if (!okMark) return err('conflict', 409);

      var flags = reviewFlags(s);
      var entry = {
        id: deps.newId(), sessionId: sid, dropId: s.dropId, nickname: nick, email: email, score: s.score,
        shots: s.shots.length, status: flags.length ? 'review' : 'verified', flags: flags, createdAt: now()
      };
      await store.addScore(entry);
      var board = await leaderboard(s.dropId, cfg.leaderboardLimit);
      var rank = entry.status === 'verified' ? await store.rankOf(s.dropId, entry.score, entry.createdAt, email) : null;
      return { ok: true, status: entry.status, nickname: nick, score: s.score, rank: rank, entries: board.entries, plays: board.plays };
    }

    async function leaderboard(dropId, limit) {
      var rows = await store.topScores(dropId || cfg.dropId, Math.min(limit || cfg.leaderboardLimit, 50));
      var plays = store.countPlays ? await store.countPlays(dropId || cfg.dropId) : null;
      return {
        ok: true, plays: plays,
        drop: { id: dropId || cfg.dropId, label: cfg.dropLabel, endsAt: cfg.dropEndsAt },
        entries: rows.map(function (r, i) { return { rank: i + 1, nickname: r.nickname, score: r.score }; })
      };
    }

    return { start: start, shot: shot, submit: submit, leaderboard: leaderboard, config: cfg, reviewFlags: reviewFlags };
  }

  /* In-memory store: used by the offline practice mode and the tests. */
  function MemoryStore() {
    var sessions = {}, scores = [];
    function bestPerEmail(dropId) {
      var best = {};
      scores.forEach(function (e) {
        if (e.dropId !== dropId || e.status !== 'verified') return;
        var b = best[e.email];
        if (!b || e.score > b.score || (e.score === b.score && e.createdAt < b.createdAt)) best[e.email] = e;
      });
      return Object.values(best).sort(function (a, b) { return b.score - a.score || a.createdAt - b.createdAt; });
    }
    return {
      createSession: async function (s) { sessions[s.sid] = JSON.parse(JSON.stringify(s)); },
      getSession: async function (sid) { return sessions[sid] ? JSON.parse(JSON.stringify(sessions[sid])) : null; },
      updateSession: async function (sid, version, patch) {
        var s = sessions[sid];
        if (!s || s.version !== version) return false;
        Object.assign(s, JSON.parse(JSON.stringify(patch)));
        s.version++;
        return true;
      },
      countRecentSessions: async function (ipHash, since) {
        return Object.values(sessions).filter(function (s) { return s.ipHash === ipHash && s.createdAt >= since; }).length;
      },
      addScore: async function (e) { scores.push(JSON.parse(JSON.stringify(e))); },
      // one row per player (email): their best verified run
      topScores: async function (dropId, limit) {
        return bestPerEmail(dropId).slice(0, limit);
      },
      rankOf: async function (dropId, score, createdAt, email) {
        return 1 + bestPerEmail(dropId).filter(function (e) {
          return e.email !== email && (e.score > score || (e.score === score && e.createdAt < createdAt));
        }).length;
      },
      countPlays: async function (dropId) {
        return Object.values(sessions).filter(function (x) { return x.dropId === dropId && x.over; }).length;
      },
      _all: function () { return { sessions: sessions, scores: scores }; }
    };
  }

  return { createGame: createGame, MemoryStore: MemoryStore, cleanNickname: cleanNickname, cleanEmail: cleanEmail, DEFAULTS: DEFAULTS };
});
